import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { hoyChicago, formatFecha, formatHoraInicio, formatFechaHoraPared } from "@/lib/format";
import { TipoBadge, EstadoBadge } from "@/components/Badges";
import { TIPO } from "@/lib/constants";
import { ContainerId } from "@/components/marca/ContainerId";
import { RutaCarga } from "@/components/marca/RutaCarga";
import { EstadoDelDia } from "@/components/marca/EstadoDelDia";
import { PageHeader, Iniciales, EmptyState } from "@/components/marca/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Search, ChevronLeft, ChevronRight, Plus, TriangleAlert, FileCheck2, FileWarning } from "lucide-react";
import { t } from "@/lib/i18n";

// Fix P (2026-10-13): la fila del panel ahora muestra el ID de carga, el tamaño, el estado operativo, la etapa en curso,
// el chofer de esa etapa, la ETA vigente y los documentos. Los avisos llevan texto además del color.
const ESTADOS_GLOBALES = ["sin_asignar", "asignada", "en_ruta", "terminada"];
const FILTROS_EXTRA = {
  con_problema: (c) => c.con_problema,
  por_reasignar: (c) => c.pendiente_reasignar,
  docs_pendientes: (c) => c.cierre_admin === "documentos_pendientes",
};

function EtaTexto({ c }) {
  const v = c.eta_vigente;
  return (
    <>
      <span className="font-mono text-[13px] block" data-testid={`eta-${c.id}`}>{v ? formatFechaHoraPared(v.eta_fecha, v.eta_hora, v.zona_horaria) : "—"}</span>
    </>
  );
}

function DocsTexto({ c, testPrefix = "" }) {
  const falt = (c.docs_faltantes || []).length;
  return (
    <div className="flex flex-col gap-0.5 items-start text-[12.5px]">
      {c.papel_estado === "ok" && <span data-testid={`${testPrefix}papel-ok-${c.id}`} className="inline-flex items-center gap-1 font-semibold text-ok"><FileCheck2 className="h-4 w-4" />{t(" POD aprobado")}</span>}
      {c.papel_estado === "warn" && <span data-testid={`${testPrefix}papel-warn-${c.id}`} className="inline-flex items-center gap-1 font-semibold text-[#8A5A0B]"><FileWarning className="h-4 w-4" />{t(" POD con observaciones")}</span>}
      {c.cierre_admin === "documentos_pendientes" && <span data-testid={`${testPrefix}docs-pendientes-${c.id}`} className="font-semibold text-danger">{t("Documents pending ({falt})", { falt })}</span>}
      {c.cierre_admin === "cerrada" && <span data-testid={`${testPrefix}docs-cerrada-${c.id}`} className="font-semibold text-ok">{t("Documentos completos")}</span>}
    </div>
  );
}

export default function PanelDia() {
  const navigate = useNavigate();
  const [fecha, setFecha] = useState(hoyChicago());
  const [estado, setEstado] = useState("todos");
  const [extra, setExtra] = useState("");
  const [choferId, setChoferId] = useState("todos");
  const [search, setSearch] = useState("");
  const [cargas, setCargas] = useState([]);
  const [contadoresEstado, setContadoresEstado] = useState({ sin_asignar: 0, asignada: 0, en_ruta: 0, terminada: 0 });
  const [choferes, setChoferes] = useState([]);
  const [ultima, setUltima] = useState(null);
  const [ahora, setAhora] = useState(Date.now());
  const buscadorRef = useRef(null);
  const pedido = useRef(0);

  useEffect(() => {
    const tt = setInterval(() => setAhora(Date.now()), 5000);
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (e.key === "/" && !["input", "textarea", "select"].includes(tag) && !e.target.isContentEditable) { e.preventDefault(); buscadorRef.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => { clearInterval(tt); window.removeEventListener("keydown", onKey); };
  }, []);
  const actualizado = ultima ? t("Actualizado hace {v0} s", { v0: Math.max(0, Math.round((ahora - ultima) / 1000)) }) : "";

  useEffect(() => {
    api.get("/choferes").then((r) => setChoferes(r.data)).catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    try {
      let url;
      if (search.trim()) {
        url = `/cargas?search=${encodeURIComponent(search.trim())}`;
      } else {
        const params = new URLSearchParams();
        if (!ESTADOS_GLOBALES.includes(estado)) params.set("fecha", fecha);
        if (estado !== "todos") params.set("estado", estado);
        if (choferId !== "todos") params.set("chofer_id", choferId);
        url = `/cargas?${params.toString()}`;
      }
      const mia = ++pedido.current;
      const filtrosContador = new URLSearchParams();
      if (choferId !== "todos") filtrosContador.set("chofer_id", choferId);
      const [{ data }, { data: totales }] = await Promise.all([
        api.get(url), api.get("/cargas/contadores?" + filtrosContador.toString()),
      ]);
      if (mia !== pedido.current) return; // llegó una respuesta más nueva: esta se descarta
      setCargas(data);
      setContadoresEstado(totales);
      setUltima(Date.now());
    } catch {
      /* ignore */
    }
  }, [fecha, estado, choferId, search]);

  usePolling(cargar, 30000);
  // Fix P (2026-10-13): al cambiar el día, el estado, el chofer o la búsqueda la lista se recarga enseguida
  // (antes esperaba hasta 30 s al siguiente refresco automático).
  const primera = useRef(true);
  useEffect(() => {
    if (primera.current) { primera.current = false; return; }
    cargar();
  }, [cargar]);

  const contadoresActuales = {
    sin_asignar: cargas.filter((c) => c.estado === "sin_asignar").length,
    asignada: cargas.filter((c) => c.estado === "asignada").length,
    en_ruta: cargas.filter((c) => c.estado === "en_ruta").length,
    terminada: cargas.filter((c) => c.estado === "terminada").length,
    con_problema: cargas.filter(FILTROS_EXTRA.con_problema).length,
    por_reasignar: cargas.filter(FILTROS_EXTRA.por_reasignar).length,
    docs_pendientes: cargas.filter(FILTROS_EXTRA.docs_pendientes).length,
  };
  const baseRef = useRef(contadoresActuales);
  if (estado === "todos" && !search.trim()) baseRef.current = contadoresActuales;
  const contadores = { ...baseRef.current, ...contadoresEstado };
  const visibles = extra ? cargas.filter(FILTROS_EXTRA[extra]) : cargas;

  const cambiarDia = (delta) => {
    const [y, m, d] = fecha.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + delta);
    setFecha(dt.toISOString().slice(0, 10));
    setSearch("");
  };

  const buscando = search.trim().length > 0;
  const todasLasFechas = !buscando && ESTADOS_GLOBALES.includes(estado);
  const vacioTexto = todasLasFechas || buscando ? t("No hay cargas con estos filtros") : t("No hay cargas para este día");

  return (
    <div className="p-4 lg:p-8 space-y-5">
      <PageHeader
        titulo={t("Panel del día")}
        subtitulo={buscando ? t("Resultados de búsqueda (todas las fechas)") : `${(todasLasFechas ? t("Todas las fechas") : formatFecha(fecha))} · ${visibles.length === 1 ? t("1 carga") : t("{n} cargas", { n: visibles.length })}`}
        acciones={<>
          <div className="flex items-center gap-1">
            <button data-testid="panel-dia-anterior" onClick={() => cambiarDia(-1)} className="h-10 w-10 flex items-center justify-center rounded-md border border-line-strong bg-white hover:bg-surface-2 disabled:opacity-50" disabled={buscando} aria-label={t("Día anterior")}>
              <ChevronLeft className="h-4 w-4" />
            </button>
            <Input
              data-testid="panel-fecha-input"
              type="date"
              value={fecha}
              onChange={(e) => { setFecha(e.target.value); setSearch(""); }}
              className="h-10 w-40"
            />
            <button data-testid="panel-dia-siguiente" onClick={() => cambiarDia(1)} className="h-10 w-10 flex items-center justify-center rounded-md border border-line-strong bg-white hover:bg-surface-2 disabled:opacity-50" disabled={buscando} aria-label={t("Día siguiente")}>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <Button data-testid="panel-nueva-carga-btn" onClick={() => navigate("/panel/cargas/nueva")}><Plus className="h-4 w-4" />{t(" Nueva carga")}</Button>
        </>}
      />

      <EstadoDelDia titulo={t("Estados de todas las fechas")} contadores={contadores} estadoActivo={estado} onEstado={(valor) => { setEstado(valor); setSearch(""); setExtra(""); }} actualizado={actualizado} extraActivo={extra} onExtra={setExtra} />

      <div className="flex flex-wrap items-end gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-3" />
          <Input
            ref={buscadorRef}
            data-testid="panel-buscador"
            placeholder={t("Buscar por contenedor o ID de carga")}
            aria-label={t("Buscar por contenedor o ID de carga")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-10"
          />
          <kbd className="hidden lg:flex absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 items-center justify-center rounded border border-line-strong bg-surface-2 text-[12px] font-mono text-ink-3">/</kbd>
        </div>
        <div>
          <Label className="block mb-1">{t("Estado")}</Label>
          <Select value={estado} onValueChange={setEstado} disabled={buscando}>
            <SelectTrigger data-testid="filtro-estado" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">{t("Todos")}</SelectItem>
              <SelectItem value="sin_asignar">{t("Sin asignar")}</SelectItem>
              <SelectItem value="asignada">{t("Asignada")}</SelectItem>
              <SelectItem value="en_ruta">{t("En ruta")}</SelectItem>
              <SelectItem value="terminada">{t("Terminada")}</SelectItem>
              <SelectItem value="cancelada">{t("Cancelada")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="block mb-1">{t("Chofer")}</Label>
          <Select value={choferId} onValueChange={setChoferId} disabled={buscando}>
            <SelectTrigger data-testid="filtro-chofer" className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">{t("Todos")}</SelectItem>
              {choferes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-line overflow-hidden">
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full text-[14px]" data-testid="panel-tabla">
            <thead>
              <tr className="bg-surface-2 text-left border-b border-line">
                <th className="w-1 p-0"></th>
                {[t("Recogida"), t("Carga"), t("Tipo"), t("Estado"), t("Etapa en curso"), t("Chofer"), t("Cliente · Riel"), "ETA", t("Documentos")].map((h) => (
                  <th key={h} className="px-3 h-10 eyebrow whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibles.length === 0 && (
                <tr><td colSpan={10}><EmptyState texto={vacioTexto} accion={<Button onClick={() => navigate("/panel/cargas/nueva")}>{t("Nueva carga")}</Button>} /></td></tr>
              )}
              {visibles.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`panel-fila-${c.id}`}
                  onClick={() => navigate(`/panel/cargas/${c.id}`)}
                  className="border-b border-[#EFECE6] last:border-0 hover:bg-[#FBF8F2] cursor-pointer h-16 transition-colors duration-150"
                >
                  <td className="p-0 w-1"><span className="block w-1 h-16" style={{ background: TIPO[c.tipo_operacion]?.color }} /></td>
                  <td className="px-3 py-2 whitespace-nowrap" data-testid={`recogida-${c.id}`}>
                    <span className="block text-ink">{formatFecha(c.fecha)}</span>
                    <span className="block text-[12.5px] text-ink-3 font-mono">{formatHoraInicio(c.hora_inicio)}</span>
                  </td>
                  <td className="px-3 py-2">
                    <span className="block font-display font-bold text-[15px] text-navy-2" data-testid={`load-id-${c.id}`}>{c.load_id || "—"}</span>
                    <ContainerId numero={c.numero_contenedor} size="sm" />
                  </td>
                  <td className="px-3 py-2"><TipoBadge tipo={c.tipo_operacion} /><span className="block text-[12.5px] text-ink-2 mt-1 font-mono" data-testid={`tamano-${c.id}`}>{c.tamano || "—"}</span></td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-1 items-start">
                      <EstadoBadge estado={c.estado} />
                      <span className="text-[12.5px] text-ink-2" data-testid={`estado-operativo-${c.id}`}>{c.estado_operativo?.texto}</span>
                      {c.con_problema && <span data-testid={`con-problema-${c.id}`} className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-danger"><TriangleAlert className="h-3.5 w-3.5" />{t(" Problema")}</span>}
                      {c.pendiente_reasignar && <span className="text-[12.5px] font-semibold text-ink-2">{t("Por reasignar")}</span>}
                      {c.contenedor_no_coincide && <span data-testid={`no-coincide-${c.id}`} className="text-[12.5px] font-semibold text-danger">{t("Contenedor no coincide: ")}<span className="font-mono">{c.contenedor_confirmado}</span></span>}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <RutaCarga carga={c} variant="mini" />
                    {c.estado_operativo?.proximo_paso && <span className="block text-[12px] text-ink-3 mt-0.5" data-testid={`proximo-paso-${c.id}`}>{t("Próximo: {proximo_paso}", { proximo_paso: c.estado_operativo.proximo_paso })}</span>}
                  </td>
                  <td className="px-3 py-2">
                    {c.chofer_nombre
                      ? <span className="flex items-center gap-2"><Iniciales nombre={c.chofer_nombre} /><span className="text-ink">{c.chofer_nombre}</span></span>
                      : <span className="text-ink-3">{t("Sin asignar")}</span>}
                  </td>
                  <td className="px-3 py-2"><span className="block text-ink">{c.cliente_nombre}</span><span className="block text-[12.5px] text-ink-3">{c.riel_nombre}</span></td>
                  <td className="px-3 py-2"><EtaTexto c={c} /></td>
                  <td className="px-3 py-2"><DocsTexto c={c} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="lg:hidden divide-y divide-[#EFECE6]" data-testid="panel-cards">
          {visibles.length === 0 && (
            <EmptyState texto={vacioTexto} accion={<Button onClick={() => navigate("/panel/cargas/nueva")}>{t("Nueva carga")}</Button>} />
          )}
          {visibles.map((c) => (
            <button
              key={c.id}
              data-testid={`panel-card-${c.id}`}
              onClick={() => navigate(`/panel/cargas/${c.id}`)}
              className="relative w-full text-left pl-5 pr-4 py-4 active:bg-surface-2"
            >
              <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: TIPO[c.tipo_operacion]?.color }} />
              <p className="mb-1.5 text-[13px] font-semibold text-ink" data-testid={`card-recogida-${c.id}`}>{formatFecha(c.fecha)} · <span className="font-mono font-normal text-ink-2">{formatHoraInicio(c.hora_inicio)}</span></p>
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2"><TipoBadge tipo={c.tipo_operacion} /><span className="text-[12.5px] font-mono text-ink-2">{c.tamano}</span></span>
                <EstadoBadge estado={c.estado} className="shrink-0" />
              </div>
              <p className="mt-1.5 font-display font-bold text-[16px] text-navy-2">{c.load_id || "—"}</p>
              <div className="mt-0.5"><ContainerId numero={c.numero_contenedor} size="lg" /></div>
              <p className="text-[13.5px] text-ink-2 mt-1">{c.estado_operativo?.texto}</p>
              <div className="mt-2"><RutaCarga carga={c} variant="mini" /></div>
              <p className="text-[14px] font-semibold text-ink mt-2 flex items-center gap-2">
                {c.chofer_nombre ? <><Iniciales nombre={c.chofer_nombre} />{c.chofer_nombre}</> : <span className="text-ink-3 font-normal">{t("Sin asignar")}</span>}
              </p>
              <p className="text-[14px] text-ink-3 mt-1">{c.cliente_nombre} · {c.riel_nombre}</p>
              <div className="mt-2 text-[12.5px] text-ink-3"><EtaTexto c={c} /></div>
              <div className="flex items-center gap-3 mt-2 flex-wrap empty:hidden">
                <DocsTexto c={c} testPrefix="card-" />
                {c.con_problema && <span data-testid={`card-con-problema-${c.id}`} className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-danger"><TriangleAlert className="h-3.5 w-3.5" />{t(" Problema")}</span>}
                {c.pendiente_reasignar && <span className="text-[12.5px] font-semibold text-ink-2">{t("Por reasignar")}</span>}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
